
import { useState, useEffect } from 'react';
import { SITE_CONFIG } from '../../../config/constants';

interface NavigationProps {
  activeSection: string;
}

export default function Navigation({ activeSection }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 200);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { id: 'home', label: 'Home' },
    { id: 'value', label: 'Value' },
    { id: 'industries', label: 'Industries' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled ? 'py-0' : 'py-2'
        }`}
      >
        {/* Top golden accent line */}
        <div
          className={`absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/80 to-transparent transition-all duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
            mounted ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
          }`}
        ></div>

        {/* Background layers */}
        <div
          className={`absolute inset-0 transition-all duration-700 ${
            isScrolled
              ? 'bg-[#080808]/90 backdrop-blur-2xl'
              : 'bg-gradient-to-b from-black/40 to-transparent'
          }`}
        ></div>

        {/* Bottom border with golden shimmer */}
        <div
          className={`absolute bottom-0 left-0 right-0 h-[1px] transition-opacity duration-700 ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-300/20 to-transparent animate-pulse"></div>
        </div>

        {/* Subtle golden glow behind nav when scrolled */}
        <div
          className={`absolute -bottom-4 left-1/4 right-1/4 h-8 bg-amber-500/5 blur-2xl transition-opacity duration-700 ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>

        <div className="relative max-w-[1400px] mx-auto px-8">
          <div className="h-20 flex items-center justify-between">
            {/* Logo Area */}
            <div
              className={`flex items-center gap-4 group cursor-pointer transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
              }`}
              style={{ transitionDelay: '300ms' }}
              onClick={() => scrollToSection('home')}
            >
              {/* Logo Text */}
              <div className="flex flex-col">
                <span className="text-white text-lg font-medium tracking-wide font-display">
                  International Intelligence
                </span>
                <div className="flex items-center gap-2 mt-0.5">
                  <div
                    className={`h-px bg-gradient-to-r from-amber-500/60 to-transparent transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      mounted ? 'w-8' : 'w-0'
                    }`}
                    style={{ transitionDelay: '800ms' }}
                  ></div>
                  <span
                    className={`text-[10px] tracking-[0.25em] text-amber-500/60 uppercase font-mono transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      mounted ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ transitionDelay: '900ms' }}
                  >
                    Premium Domain
                  </span>
                </div>
              </div>
            </div>

            {/* Center Navigation */}
            <div className="hidden md:flex items-center">
              {/* Nav container with subtle border */}
              <div
                className={`relative flex items-center gap-0.5 px-1.5 py-1.5 rounded-full border border-white/[0.06] bg-white/[0.02] transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                }`}
                style={{ transitionDelay: '500ms' }}
              >
                {/* Golden shimmer line inside */}
                <div className="absolute top-0 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
                <div className="absolute bottom-0 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-amber-500/10 to-transparent"></div>

                {menuItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`relative px-5 py-2 text-[11px] tracking-[0.14em] uppercase transition-all duration-500 cursor-pointer rounded-full font-mono ${
                      activeSection === item.id
                        ? 'text-amber-300'
                        : 'text-white/40 hover:text-white/70'
                    }`}
                    style={{
                      opacity: mounted ? 1 : 0,
                      transform: mounted ? 'translateY(0)' : 'translateY(-8px)',
                      transition: `opacity 1000ms cubic-bezier(0.22,1,0.36,1) ${
                        650 + index * 120
                      }ms, transform 1000ms cubic-bezier(0.22,1,0.36,1) ${
                        650 + index * 120
                      }ms, color 400ms`,
                    }}
                  >
                    {/* Active background */}
                    {activeSection === item.id && (
                      <span className="absolute inset-0 rounded-full bg-gradient-to-b from-amber-500/15 to-amber-600/5 border border-amber-500/20"></span>
                    )}
                    {/* Hover glow */}
                    {activeSection === item.id && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-3 bg-amber-500/30 blur-md"></span>
                    )}
                    <span className="relative">{item.label}</span>
                    {/* Active dot */}
                    {activeSection === item.id && (
                      <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-400 rounded-full shadow-[0_0_6px_rgba(245,158,11,0.6)]"></span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side - CTA */}
            <div className="hidden md:flex items-center gap-4">
              {/* Status indicator */}
              <div
                className="flex items-center gap-2 mr-2"
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateX(0)' : 'translateX(10px)',
                  transition:
                    'opacity 1100ms cubic-bezier(0.22,1,0.36,1) 800ms, transform 1100ms cubic-bezier(0.22,1,0.36,1) 800ms',
                }}
              >
                <div className="relative w-1.5 h-1.5">
                  <div className="absolute inset-0 bg-emerald-400 rounded-full"></div>
                  <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-40"></div>
                </div>
                <span className="text-[10px] tracking-[0.15em] text-white/30 uppercase">
                  Available
                </span>
              </div>

              {/* Separator */}
              <div
                className="w-[1px] h-5 bg-gradient-to-b from-transparent via-amber-500/20 to-transparent"
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'scaleY(1)' : 'scaleY(0)',
                  transition:
                    'opacity 1000ms cubic-bezier(0.22,1,0.36,1) 950ms, transform 1000ms cubic-bezier(0.22,1,0.36,1) 950ms',
                }}
              ></div>

              {/* CTA Button */}
              <button
                onClick={() => scrollToSection('contact')}
                className="group relative flex items-center gap-2.5 px-6 py-2.5 cursor-pointer overflow-hidden rounded-full"
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateX(0)' : 'translateX(12px)',
                  transition:
                    'opacity 1100ms cubic-bezier(0.22,1,0.36,1) 1050ms, transform 1100ms cubic-bezier(0.22,1,0.36,1) 1050ms',
                }}
              >
                {/* Button background */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-amber-500/15 to-amber-600/10 group-hover:from-amber-500/30 group-hover:via-amber-500/25 group-hover:to-amber-600/15 transition-all duration-500"></div>
                {/* Button border */}
                <div className="absolute inset-0 rounded-full border border-amber-500/30 group-hover:border-amber-400/50 transition-colors duration-500"></div>
                {/* Top golden line */}
                <div className="absolute top-0 left-4 right-4 h-[1px] bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"></div>
                {/* Shine sweep effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                {/* Glow */}
                <div className="absolute -bottom-2 left-1/4 right-1/4 h-4 bg-amber-500/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <span className="relative text-[11px] tracking-[0.12em] text-amber-300 uppercase font-medium whitespace-nowrap">
                  Make Offer
                </span>
                <i className="relative ri-arrow-right-up-line text-amber-400 text-sm group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"></i>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden relative w-10 h-10 flex items-center justify-center text-white/70 hover:text-white cursor-pointer rounded-lg hover:bg-white/5 transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] border border-white/[0.06] ${
                mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
              style={{ transitionDelay: '500ms' }}
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Decorative side golden dots */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 left-4 w-1 h-1 rounded-full bg-amber-500/30 transition-opacity duration-700 ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>
        <div
          className={`absolute top-1/2 -translate-y-1/2 right-4 w-1 h-1 rounded-full bg-amber-500/30 transition-opacity duration-700 ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed top-20 right-0 bottom-0 w-80 z-40 transform transition-transform duration-300 md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile menu background */}
        <div className="absolute inset-0 bg-[#0a0a0a]/98 backdrop-blur-xl"></div>
        {/* Left golden border */}
        <div className="absolute top-0 left-0 bottom-0 w-[1px] bg-gradient-to-b from-amber-500/40 via-amber-500/10 to-transparent"></div>

        <div className="relative flex flex-col p-8 h-full">
          {/* Menu Items */}
          <div className="space-y-2 mb-8">
            {menuItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`w-full text-left px-4 py-3.5 text-sm tracking-[0.1em] uppercase transition-all cursor-pointer rounded-xl ${
                  activeSection === item.id
                    ? 'text-amber-300 bg-amber-500/10 border border-amber-500/20'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <span className="flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    {activeSection === item.id && (
                      <span className="w-1.5 h-1.5 bg-amber-400 rounded-full shadow-[0_0_6px_rgba(245,158,11,0.6)]"></span>
                    )}
                    {item.label}
                  </span>
                  {activeSection === item.id && (
                    <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                  )}
                </span>
              </button>
            ))}
          </div>

          {/* Divider with golden accent */}
          <div className="relative h-px w-full mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
          </div>

          {/* CTA */}
          <button
            onClick={() => scrollToSection('contact')}
            className="relative w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl cursor-pointer overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-600"></div>
            <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-amber-300/60 to-transparent"></div>
            <span className="relative text-sm tracking-[0.1em] text-black font-medium uppercase whitespace-nowrap">
              Make Offer
            </span>
            <i className="relative ri-arrow-right-up-line text-black"></i>
          </button>

          {/* Contact Info */}
          <div className="mt-auto pt-8">
            <p className="text-[10px] tracking-[0.2em] text-white/30 uppercase mb-4">
              Contact
            </p>
            <a
              href={`mailto:${SITE_CONFIG.contact.email}`}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-amber-500/50"></i>
              {SITE_CONFIG.contact.email}
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden cursor-pointer"
        ></div>
      )}
    </>
  );
}
